import { createSlice } from '@reduxjs/toolkit';

import { WithdrawAvailability } from '../views/Earnings/Earnings.types';
import {
  getMyHubsSummary,
  getMyHubsTravel,
  getMyHubsSubscriptions,
  getMyHubsSaveAndTravelSubscriptions,
  sendGift,
  getMyHubsGoldenCard,
  getMyHubsSubscriptionsBundle,
  getMyHubsSuperSales,
  getRevolutionSubscriptions,
  getMyHubsVip,
  getMyHubsAccessPacks
} from '../views/Portfolio/Portfolio.actions';
import {
  CurrentVirtualOfficeLicence,
  GoldenCard,
  PackageBundleSubscription,
  PackageSubscription,
  PortfolioMonthlyActivity,
  PortfolioWalletSummary,
  TravelPackages
} from '../views/Portfolio/Portfolio.types';

export interface PortfolioSliceType {
  loading: boolean;
  loadingHubs: boolean;
  loadingSummary: boolean;
  giftLoading: boolean;
  travelPackages: TravelPackages | null;
  subscriptions: PackageSubscription[] | null;
  saveAndTravelSubscriptions: PackageSubscription[] | null;
  subscriptionsBundle: PackageBundleSubscription[] | null;
  superSales: any;
  accessPacks: any;
  revolutionSubscriptions: any;
  vip: any;
  goldenCard: any;
  summary: any;
  currentVirtualOfficeLicence: CurrentVirtualOfficeLicence | null;
  monthlyActivity: PortfolioMonthlyActivity | null;
  lifetimeRank: string;
  qualifiedRank: string;
  walletSummary: PortfolioWalletSummary | null;
}

const initialState = {
  loading: false,
  loadingHubs: false,
  loadingSummary: false,
  giftLoading: false,
  travelPackages: null,
  subscriptions: null,
  saveAndTravelSubscriptions: null,
  subscriptionsBundle: null,
  goldenCard: null,
  superSales: null,
  accessPacks: null,
  revolutionSubscriptions: null,
  summary: {},
  currentVirtualOfficeLicence: null,
  monthlyActivity: null,
  lifetimeRank: '',
  qualifiedRank: '',
  walletSummary: null
} as PortfolioSliceType;

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    emptyMyHubsDataWhenPackageBuy(state) {
      state.travelPackages = null;
      state.walletSummary = null;
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getMyHubsSummary.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getMyHubsSummary.fulfilled, (state, { payload }) => {
      state.loading = false;

      state.currentVirtualOfficeLicence =
        payload?.currentVirtualOfficeLicence || null;
      state.lifetimeRank = payload?.lifetimeRank || '';
      state.qualifiedRank = payload?.qualifiedRank || '';
      state.walletSummary = payload?.walletSummary || null;
    });
    builder.addCase(getMyHubsSummary.rejected, (state) => {
      state.loading = false;
    });

    // TRAVEL PACKAGES
    builder.addCase(getMyHubsTravel.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsTravel.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.travelPackages = payload || null;
    });
    builder.addCase(getMyHubsTravel.rejected, (state) => {
      state.loadingHubs = false;
    });

    // ACCESS PACKS
    builder.addCase(getMyHubsAccessPacks.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsAccessPacks.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.accessPacks = payload || null;
    });
    builder.addCase(getMyHubsAccessPacks.rejected, (state) => {
      state.loadingHubs = false;
    });

    //MY_HUBS_SUBSCRIPTIONS
    builder.addCase(getMyHubsSubscriptions.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsSubscriptions.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.subscriptions = payload || null;
    });
    builder.addCase(getMyHubsSubscriptions.rejected, (state) => {
      state.loadingHubs = false;
    });

    //SAVE AND TRAVEL SUBSCRIPTION

    builder.addCase(getMyHubsSaveAndTravelSubscriptions.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(
      getMyHubsSaveAndTravelSubscriptions.fulfilled,
      (state, { payload }) => {
        state.loadingHubs = false;
        state.saveAndTravelSubscriptions = payload || null;
      }
    );
    builder.addCase(getMyHubsSaveAndTravelSubscriptions.rejected, (state) => {
      state.loadingHubs = false;
    });

    //MY_HUBS_BUNDLE_SUBSCRIPTIONS
    builder.addCase(getMyHubsSubscriptionsBundle.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(
      getMyHubsSubscriptionsBundle.fulfilled,
      (state, { payload }) => {
        state.loadingHubs = false;
        state.subscriptionsBundle = payload || null;
      }
    );
    builder.addCase(getMyHubsSubscriptionsBundle.rejected, (state) => {
      state.loadingHubs = false;
    });

    //SEND GIFT
    builder.addCase(sendGift.pending, (state) => {
      state.giftLoading = true;
    });
    builder.addCase(sendGift.fulfilled, (state, { payload }) => {
      state.giftLoading = false;

      if (state.subscriptionsBundle) {
        const index = state.subscriptionsBundle.findIndex(
          (p) => p.id === payload?.giftOrderId
        );
        if (index !== -1) {
          state.subscriptionsBundle[index].remainingTravelutionSubscriptions =
            state.subscriptionsBundle[index].remainingTravelutionSubscriptions -
            1;
          state.subscriptionsBundle[index].giftedTravelutionSubscriptions.push(
            payload?.giftedTravelutionSubscription
          );
        }
      }
    });
    builder.addCase(sendGift.rejected, (state) => {
      state.giftLoading = false;
    });

    // SUPER SALES
    builder.addCase(getMyHubsSuperSales.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsSuperSales.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.superSales = payload || null;
    });
    builder.addCase(getMyHubsSuperSales.rejected, (state) => {
      state.loadingHubs = false;
    });

    builder.addCase(getRevolutionSubscriptions.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(
      getRevolutionSubscriptions.fulfilled,
      (state, { payload }) => {
        state.loadingHubs = false;
        state.revolutionSubscriptions = payload || null;
      }
    );
    builder.addCase(getRevolutionSubscriptions.rejected, (state) => {
      state.loadingHubs = false;
    });

    // VIP
    builder.addCase(getMyHubsVip.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsVip.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.vip = payload.orders || null;
    });
    builder.addCase(getMyHubsVip.rejected, (state) => {
      state.loadingHubs = false;
    });

    // SUPER SALES
    builder.addCase(getMyHubsGoldenCard.pending, (state) => {
      // state.loadingHubs = true;
    });
    builder.addCase(getMyHubsGoldenCard.fulfilled, (state, { payload }) => {
      // state.loadingHubs = false;
      state.goldenCard = payload || null;
    });
    builder.addCase(getMyHubsGoldenCard.rejected, (state) => {
      // state.loadingHubs = false;
    });
  }
});

export const { emptyMyHubsDataWhenPackageBuy } = portfolioSlice.actions;

export default portfolioSlice.reducer;
